import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { ThemeProviderContext } from "@/contexts/theme-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  openSupportConversation,
  openSupportEmail,
  SUPPORT_EMAIL,
} from "@/lib/support";
import { useAdminModeCountdown } from "@/hooks/useAdminModeCountdown";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { useClerk, useUser } from "@clerk/react";
import { usePostHog } from "posthog-js/react";
import { AdminTeamDTO } from "@shaarp/common";
import { CheckIcon, MoonIcon, SunIcon } from "lucide-react";
import { useContext, useState } from "react";
import { LuComputer } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { DialogAdminTeamCreation } from "@/components/admin/dialog-admin-team-creation";
import { DialogAdminUserInvitation } from "@/components/admin/dialog-admin-user-invitation";
import NavTeamSwitcher from "@/components/navbar/nav-team-switcher";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";

/**
 * The account menu of the mobile sidebar. `onLeave` runs before an item that
 * leaves the sidebar — a screen, the account modal, support — so the sidebar's
 * sheet is closed first: left open, it would stay over the next screen, or its
 * focus trap would swallow the first tap in the modal. The admin dialogs are
 * the exception: their state lives in this menu, inside the sheet, so closing
 * the sheet would unmount them before they show. A Radix dialog stacks over
 * the sheet and takes the focus from it.
 *
 * The team and help live in the sidebar's sections; `hasTeamAndHelp` keeps
 * them here where there is no sidebar — onboarding and the team screens.
 */
export function NavUserDropdownMobile({
  onLeave = () => {},
  hasTeamAndHelp = true,
}: {
  readonly onLeave?: () => void;
  readonly hasTeamAndHelp?: boolean;
} = {}) {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const { t } = useTranslation();
  const { isAdminMode, setAdminMode } = useAdminMode();
  const adminModeCountdown = useAdminModeCountdown();
  const { isAdmin } = useTeams();
  const themeContext = useContext(ThemeProviderContext);
  const { toast } = useToast();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const [showNewTeamDialog, setShowNewTeamDialog] = useState(false);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  // Stacked over the open sidebar: the back gesture closes the dialog first,
  // instead of closing the sidebar and unmounting the half-filled form with it.
  useCloseOnHistoryBack(showNewTeamDialog, () => setShowNewTeamDialog(false));
  useCloseOnHistoryBack(showNewUserDialog, () => setShowNewUserDialog(false));

  const onNewTeamCreated = (newTeam: AdminTeamDTO) => {
    toast({
      title: t("admin.team.creation.toast.success.title"),
      description: t("admin.team.creation.toast.success.description", {
        name: newTeam.name,
      }),
    });
    setShowNewTeamDialog(false);
  };

  return (
    <>
      <Menubar className="border-0">
        <MenubarMenu>
          <MenubarTrigger className="flex overflow-hidden relative justify-center items-center p-0 rounded-full cursor-pointer size-11">
            <Avatar className="w-8 h-8">
              <AvatarImage
                src={user?.imageUrl}
                alt={user?.fullName || ""}
                className="object-cover"
              />
            </Avatar>
          </MenubarTrigger>
          <MenubarContent>
            <MenubarItem disabled className="data-disabled:opacity-100">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user?.fullName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem
              onClick={() => {
                onLeave();
                openUserProfile();
              }}
            >
              {t("components.navUserDropdownMobile.accountAndSecurity")}
            </MenubarItem>
            <MenubarItem
              onClick={() => {
                onLeave();
                navigate(`/referrals`);
              }}
            >
              {t("userNav.referral")}
            </MenubarItem>
            <MenubarSeparator />
            <MenubarSub>
              <MenubarSubTrigger>
                {t("components.navUserDropdownMobile.theme.title")}
              </MenubarSubTrigger>
              <MenubarSubContent>
                <MenubarItem onClick={() => themeContext.setTheme("system")}>
                  <LuComputer className="mr-2 w-3 h-3" />
                  {t("components.navUserDropdownMobile.theme.automatic")}
                  <CheckIcon
                    className={cn(
                      "ml-3 h-4 w-4",
                      themeContext.theme === "system"
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </MenubarItem>
                <MenubarItem onClick={() => themeContext.setTheme("light")}>
                  <SunIcon className="mr-2 w-3 h-3" />
                  {t("components.navUserDropdownMobile.theme.light")}
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      themeContext.theme === "light"
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </MenubarItem>
                <MenubarItem onClick={() => themeContext.setTheme("dark")}>
                  <MoonIcon className="mr-2 w-3 h-3" />
                  {t("components.navUserDropdownMobile.theme.dark")}
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      themeContext.theme === "dark"
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </MenubarItem>
              </MenubarSubContent>
            </MenubarSub>
            {isAdmin && (
              <>
                <MenubarItem
                  onClick={(e) => e.preventDefault()}
                  className="bg-warning-foreground"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span>{t("userNav.admin.mode")}</span>
                      {isAdminMode && adminModeCountdown && (
                        <span className="text-[10px] text-muted-foreground">
                          {adminModeCountdown}
                        </span>
                      )}
                    </div>
                    <Switch
                      className="ml-auto"
                      checked={isAdminMode}
                      onCheckedChange={() => setAdminMode(!isAdminMode)}
                    />
                  </div>
                </MenubarItem>
              </>
            )}
            {isAdminMode && (
              <>
                <MenubarItem
                  onClick={() => setShowNewTeamDialog(true)}
                  className="bg-warning-foreground"
                >
                  {t("teamSwitcher.newTeam")}
                </MenubarItem>
                <MenubarItem
                  onClick={() => setShowNewUserDialog(true)}
                  className="bg-warning-foreground"
                >
                  {t("teamSwitcher.newUser")}
                </MenubarItem>
              </>
            )}
            {hasTeamAndHelp && (
              <>
                <MenubarSeparator />
                <MenubarSub>
                  <MenubarSubTrigger>
                    {t("components.navUserDropdownMobile.help.title")}
                  </MenubarSubTrigger>
                  <MenubarSubContent>
                    <MenubarItem disabled className="data-disabled:opacity-100">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {t(
                            "components.navUserDropdownMobile.help.supportTitle",
                          )}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {SUPPORT_EMAIL}
                        </p>
                      </div>
                    </MenubarItem>
                    <MenubarItem
                      onClick={() => {
                        onLeave();
                        openSupportConversation();
                      }}
                    >
                      {t("components.navUserDropdownMobile.help.liveChat")}
                    </MenubarItem>
                    <MenubarItem
                      onClick={() => {
                        onLeave();
                        openSupportEmail();
                      }}
                    >
                      {t("components.navUserDropdownMobile.help.sendEmail")}
                    </MenubarItem>
                  </MenubarSubContent>
                </MenubarSub>
                <MenubarSeparator />
                <NavTeamSwitcher />
              </>
            )}
            <MenubarSeparator />
            {/* As on the desktop: the next person on this phone must not be
                counted as this user, nor in this team. */}
            <MenubarItem
              onClick={() => {
                posthog?.reset();
                void signOut();
              }}
            >
              {t("components.navUserDropdownMobile.signOut")}
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>

      <DialogAdminTeamCreation
        open={showNewTeamDialog}
        onCreated={onNewTeamCreated}
        onClose={() => setShowNewTeamDialog(false)}
      />
      <DialogAdminUserInvitation
        open={showNewUserDialog}
        onClose={() => setShowNewUserDialog(false)}
      />
    </>
  );
}
